import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Play, Plus, Trash2 } from 'lucide-react';
import {
  DUNNING_ACTIONS,
  DUNNING_ACTION_LABELS,
  MEMBER_STATUS_LABELS,
  formatCents,
  type DunningAction,
  type DunningRun,
  type DunningStep,
  type DunningStepInput,
  type OverdueMember,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDocument } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { FormSection, Field } from '@/components/FormSection';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { MEMBER_STATUS_TONE } from './Associados';

/**
 * Inadimplência do clube.
 *
 * Duas coisas na mesma tela, porque são a mesma conversa: **quem deve** (a
 * lista que o balcão usa para ligar) e **a régua** (o que o sistema faz
 * sozinho conforme o atraso cresce).
 *
 * A régua roda todo dia às 04:10. O botão aqui é para conferir antes e para
 * quando o operador quiser escalar na hora — e é idempotente: um degrau roda
 * uma vez por parcela, por mais que se clique.
 */

/** Um degrau só é perigoso a partir do que ele faz. */
const ACTION_TONE: Record<DunningAction, 'info' | 'warn' | 'bad'> = {
  AVISAR: 'info',
  MARCAR_INADIMPLENTE: 'warn',
  BLOQUEAR_ACESSO: 'bad',
};

/** "3 dias antes" lê melhor que "-3". */
function quando(offsetDays: number): string {
  if (offsetDays < 0) return `${Math.abs(offsetDays)} dias antes`;
  if (offsetDays === 0) return 'no vencimento';
  return `${offsetDays} dias depois`;
}

export function InadimplenciaPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [editando, setEditando] = useState<DunningStep | null>(null);
  const [criando, setCriando] = useState(false);
  const [confirmandoRun, setConfirmandoRun] = useState(false);
  const [resultado, setResultado] = useState<DunningRun | null>(null);

  const overdueQuery = useQuery({
    queryKey: ['dunning', 'overdue'],
    queryFn: () =>
      api.get<{ data: OverdueMember[]; totals: { members: number; cents: number } }>(
        '/dunning/overdue',
      ),
  });

  const stepsQuery = useQuery({
    queryKey: ['dunning', 'steps'],
    queryFn: () => api.get<{ data: DunningStep[] }>('/dunning/steps'),
  });

  const previewQuery = useQuery({
    queryKey: ['dunning', 'preview'],
    queryFn: () => api.get<DunningRun>('/dunning/preview'),
  });

  const rodar = useMutation({
    mutationFn: () => api.post<DunningRun>('/dunning/run', {}),
    onSuccess: (run) => {
      setResultado(run);
      setConfirmandoRun(false);
      void queryClient.invalidateQueries({ queryKey: ['dunning'] });
      void queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });

  const overdue = overdueQuery.data;
  const steps = stepsQuery.data?.data ?? [];
  const aFazer = previewQuery.data?.lines ?? [];

  const columns: Array<Column<OverdueMember & { id: string }>> = [
    { key: 'registration', label: 'Matrícula', mono: true, render: (row) => row.registration },
    { key: 'memberName', label: 'Associado', strong: true, render: (row) => row.memberName },
    {
      key: 'document',
      label: 'CPF',
      mono: true,
      render: (row) => (row.document ? formatDocument(row.document) : '—'),
    },
    { key: 'phone', label: 'WhatsApp', mono: true, render: (row) => blank(row.phone) },
    {
      key: 'worstDaysOverdue',
      label: 'Atraso',
      right: true,
      mono: true,
      render: (row) => (
        <span style={{ color: row.worstDaysOverdue > 30 ? T.red : T.amber }}>
          {row.worstDaysOverdue} dias
        </span>
      ),
    },
    {
      key: 'overdueCount',
      label: 'Parcelas',
      right: true,
      mono: true,
      render: (row) => String(row.overdueCount),
    },
    {
      key: 'overdueCents',
      label: 'Em aberto',
      right: true,
      mono: true,
      strong: true,
      render: (row) => formatCents(row.overdueCents),
    },
    {
      key: 'status',
      label: 'Situação',
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <StatusBadge kind={MEMBER_STATUS_TONE[row.status]}>
            {MEMBER_STATUS_LABELS[row.status]}
          </StatusBadge>
          {row.accessBlockedAt && (
            <StatusBadge kind="bad">
              <Ban size={11} aria-hidden /> Entrada bloqueada
            </StatusBadge>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Inadimplência"
        actions={
          can('inadimplencia:criar') && aFazer.length > 0 ? (
            <Button icon={Play} variant="primary" onClick={() => setConfirmandoRun(true)}>
              Rodar a régua ({aFazer.length})
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {rodar.isError && (
          <div className="alert alert--bad" role="alert" style={{ marginBottom: 18 }}>
            {errorMessage(rodar.error)}
          </div>
        )}

        {resultado && <ResultadoDaRegua run={resultado} />}

        {overdueQuery.isPending && <LoadingState />}
        {overdueQuery.isError && (
          <ErrorState
            message={errorMessage(overdueQuery.error)}
            onRetry={() => void overdueQuery.refetch()}
          />
        )}

        {overdue && (
          <>
            <div className="metrics" style={{ marginBottom: 18 }}>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Associados devendo</span>
                  {overdue.totals.members > 0 && (
                    <AlertTriangle size={15} color={T.amber} aria-hidden />
                  )}
                </div>
                <div className="metric__value">{overdue.totals.members}</div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">Em aberto</span>
                </div>
                <div
                  className={`metric__value${overdue.totals.cents > 0 ? ' metric__value--bad' : ''}`}
                >
                  {formatCents(overdue.totals.cents)}
                </div>
              </div>
              <div className="metric">
                <div className="metric__head">
                  <span className="metric__label">A régua vai agir em</span>
                </div>
                <div className="metric__value">{aFazer.length}</div>
              </div>
            </div>

            {overdue.data.length === 0 ? (
              <EmptyState
                title="Ninguém em atraso"
                description="Nenhum associado com mensalidade vencida. A régua continua rodando todo dia às 04:10."
              />
            ) : (
              <SmartDataTable
                caption="Associados em atraso"
                columns={columns}
                rows={overdue.data.map((row) => ({ ...row, id: row.memberId }))}
              />
            )}
          </>
        )}

        <Degraus
          steps={steps}
          podeEditar={can('inadimplencia:editar')}
          onNovo={() => setCriando(true)}
          onEditar={setEditando}
        />
      </div>

      <FormularioDoDegrau
        open={criando || editando !== null}
        step={editando}
        onClose={() => {
          setCriando(false);
          setEditando(null);
        }}
        onSaved={() => {
          setCriando(false);
          setEditando(null);
          void queryClient.invalidateQueries({ queryKey: ['dunning'] });
        }}
      />

      <Modal
        open={confirmandoRun}
        onClose={() => setConfirmandoRun(false)}
        title="Rodar a régua agora?"
        description={`${aFazer.length} degrau(s) vão ser aplicados aos associados em atraso.`}
        footer={
          <>
            <Button onClick={() => setConfirmandoRun(false)}>Cancelar</Button>
            <Button variant="primary" onClick={() => rodar.mutate()} disabled={rodar.isPending}>
              {rodar.isPending ? 'Rodando…' : 'Rodar'}
            </Button>
          </>
        }
      >
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
          Isto <strong>avisa o associado</strong>, muda situação e fecha a catraca conforme os
          degraus abaixo. Confira a lista antes: mensagem mandada não volta atrás.
        </p>
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5, marginTop: 8 }}>
          Um degrau roda uma vez por parcela — clicar duas vezes não manda duas mensagens.
        </p>
      </Modal>
    </>
  );
}

/** O que a execução fez, incluindo o que ela não conseguiu. */
function ResultadoDaRegua({ run }: { run: DunningRun }) {
  const semAgir = run.lines.filter((line) => line.detail !== null);

  return (
    <div style={{ marginBottom: 18, display: 'grid', gap: 10 }}>
      <div className="alert alert--ok" role="status">
        {run.lines.length} degrau(s) aplicado(s)
        {run.released.length > 0 ? ` · ${run.released.length} associado(s) liberado(s)` : ''}.
      </div>

      {semAgir.length > 0 && (
        <div className="alert alert--warn" role="alert">
          <strong>{semAgir.length} degrau(s) não conseguiram agir:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
            {semAgir.map((line) => (
              <li key={`${line.stepId}-${line.installmentId}`}>
                {line.registration} · {line.memberName}: {line.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Degraus({
  steps,
  podeEditar,
  onNovo,
  onEditar,
}: {
  steps: DunningStep[];
  podeEditar: boolean;
  onNovo: () => void;
  onEditar: (step: DunningStep) => void;
}) {
  return (
    <div style={{ marginTop: 28 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <div>
          <h2 style={{ fontSize: 14, margin: 0 }}>A régua</h2>
          <p className="muted" style={{ fontSize: 12.5, margin: '4px 0 0' }}>
            Contado a partir do vencimento. O sistema aplica cada degrau uma vez, quando o atraso
            alcança o dia dele.
          </p>
        </div>
        {podeEditar && (
          <Button icon={Plus} onClick={onNovo}>
            Novo degrau
          </Button>
        )}
      </div>

      {steps.length === 0 ? (
        <EmptyState
          title="Nenhum degrau configurado"
          description="Sem degraus, a régua não faz nada: ninguém é avisado, ninguém é marcado, ninguém é bloqueado. Um começo comum: avisar 3 dias antes, avisar no dia seguinte ao vencimento, marcar inadimplente aos 15 dias e bloquear a entrada aos 30."
        />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {steps.map((step) => (
            <div
              key={step.id}
              className="card"
              style={{
                padding: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                opacity: step.isActive ? 1 : 0.55,
              }}
            >
              <div style={{ minWidth: 130 }}>
                <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>
                  {quando(step.offsetDays)}
                </span>
              </div>
              <StatusBadge kind={ACTION_TONE[step.action]}>
                {DUNNING_ACTION_LABELS[step.action]}
              </StatusBadge>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13 }}>{step.label}</div>
                {step.action === 'AVISAR' && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    modelo <span className="mono">{step.templateName}</span> ·{' '}
                    {step.templateLanguage}
                  </div>
                )}
              </div>
              <span className="muted" style={{ fontSize: 12 }}>
                {step.timesRun === 0 ? 'nunca rodou' : `${step.timesRun}×`}
              </span>
              {!step.isActive && <StatusBadge kind="mute">Desligado</StatusBadge>}
              {podeEditar && (
                <Button onClick={() => onEditar(step)}>Editar</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FormularioDoDegrau({
  open,
  step,
  onClose,
  onSaved,
}: {
  open: boolean;
  step: DunningStep | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [offsetDays, setOffsetDays] = useState('7');
  const [action, setAction] = useState<DunningAction>('AVISAR');
  const [label, setLabel] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [carregado, setCarregado] = useState<string | null>(null);

  const chave = open ? (step?.id ?? 'novo') : null;
  if (chave !== carregado) {
    setCarregado(chave);
    setOffsetDays(step ? String(step.offsetDays) : '7');
    setAction(step?.action ?? 'AVISAR');
    setLabel(step?.label ?? '');
    setTemplateName(step?.templateName ?? '');
    setIsActive(step?.isActive ?? true);
  }

  const queryClient = useQueryClient();

  const salvar = useMutation({
    mutationFn: (body: DunningStepInput) =>
      step
        ? api.patch<DunningStep>(`/dunning/steps/${step.id}`, body)
        : api.post<DunningStep>('/dunning/steps', body),
    onSuccess: onSaved,
  });

  const excluir = useMutation({
    mutationFn: () => api.delete(`/dunning/steps/${step!.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dunning'] });
      onSaved();
    },
  });

  const errors = fieldErrors(salvar.error);
  const precisaModelo = action === 'AVISAR';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={step ? `Editar "${step.label}"` : 'Novo degrau'}
      description="Contado a partir do vencimento: negativo avisa antes, zero no dia."
      footer={
        <>
          {step && (
            <Button
              icon={Trash2}
              onClick={() => excluir.mutate()}
              disabled={excluir.isPending}
              style={{ marginRight: 'auto' }}
            >
              Desligar
            </Button>
          )}
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={salvar.isPending || label.trim().length < 3}
            onClick={() =>
              salvar.mutate({
                offsetDays: Number(offsetDays) || 0,
                action,
                label: label.trim(),
                templateName: templateName.trim() || null,
                templateLanguage: 'pt_BR',
                isActive,
              })
            }
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      {salvar.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(salvar.error)}
        </div>
      )}

      <FormSection>
        <div className="grid-2">
          <Field label="Dias do vencimento" htmlFor="degrau-dias" error={errors.offsetDays} hint="negativo = antes">
            <input
              id="degrau-dias"
              className="input input--mono"
              inputMode="numeric"
              value={offsetDays}
              onChange={(event) => setOffsetDays(event.target.value.replace(/[^\d-]/g, ''))}
            />
          </Field>
          <Field label="O que fazer" htmlFor="degrau-acao" error={errors.action}>
            <select
              id="degrau-acao"
              className="input"
              value={action}
              onChange={(event) => setAction(event.target.value as DunningAction)}
            >
              {DUNNING_ACTIONS.map((value) => (
                <option key={value} value={value}>
                  {DUNNING_ACTION_LABELS[value]}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Nome do degrau" htmlFor="degrau-label" error={errors.label}>
          <input
            id="degrau-label"
            className="input"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Lembrete 3 dias antes"
          />
        </Field>

        {precisaModelo && (
          <Field
            label="Modelo aprovado na Meta"
            htmlFor="degrau-template"
            error={errors.templateName}
            hint="Obrigatório: a Meta só deixa a empresa começar conversa por modelo aprovado"
          >
            <input
              id="degrau-template"
              className="input input--mono"
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              placeholder="cobranca_mensalidade"
            />
          </Field>
        )}

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            Degrau ligado
            <span className="muted" style={{ display: 'block', fontSize: 12 }}>
              Desligado, ele fica na régua mas não age. O que ele já fez continua no histórico.
            </span>
          </span>
        </label>
      </FormSection>
    </Modal>
  );
}
